/**
 * 시설 평가등급 표기 헬퍼.
 *
 * 영구지침: docs/develop/frontend/facility-detail-evaluation-spec.md
 *
 * 공식 평가 데이터는 (장기요양기관기호 × 급여종류) 단위다. 한 기관이 방문요양·방문간호를
 * 함께 운영하면 급여종류별로 평가등급이 다르므로, 평가 정보를 노출할 때는
 * 연도·회차(평가구분)와 **급여종류**를 함께 표기해야 한다.
 * 급여종류를 감춰두었다가 임포터가 다른 급여의 값을 덮어쓴 버그를 아무도 눈으로 잡지 못해
 * 민원이 발생했다(2026-09 사고).
 *
 * 출처 기관명은 항상 국민건강보험공단이다. 심평원(건강보험심사평가원) 데이터는
 * 요양병원 진료시간·병상·주차 등 병원 정보에만 쓰이며 평가등급과 무관하다.
 *
 * 차용 표기(§2.5): 평가 CSV에 시설 유형의 급여종류 행이 없는 시설은 같은 기관의 다른 급여종류
 * 평가를 유지하고 그 급여종류를 `evaluationServiceKind` 에 기록한다. 헬퍼에 `facilityType` 을
 * 넘기면 자기 급여종류가 아닌 평가를 `… 급여 기준` / `(… 급여)` 로 구분하고, 입소시설 급여종류는
 * 표시 이름(§2.6)으로 바꾼다. `facilityType` 을 넘기지 않으면 기존 표기를 그대로 쓴다(하위 호환 —
 * 어드민은 원본 값을 보여 주기 위해 넘기지 않는다).
 */

/** 평가등급·평가점수의 출처 기관명 (고정). 화면에 직접 하드코딩하지 말고 이 상수를 쓴다. */
export const EVALUATION_SOURCE_ORG = '국민건강보험공단'

/** 앞뒤 공백 제거 후 빈 값이면 null. */
function clean(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * 급여종류 정규화. 공공데이터 CSV가 `10.방문요양` 처럼 숫자 코드 접두사를 붙여 내려주는 경우가
 * 있어 선행 `숫자 + 구분자` 를 제거한다. 제거 결과가 비면 원문을 그대로 쓴다.
 */
function normalizeServiceKind(value: string | null | undefined): string | null {
  const raw = clean(value)
  if (raw === null) return null
  const stripped = raw.replace(/^\d+\s*[.\-_)\s]\s*/, '').trim()
  return stripped.length > 0 ? stripped : raw
}

/**
 * 평가 CSV 급여종류 → 그 급여종류가 "자기 급여"인 시설 유형.
 * 백엔드 `FacilityType.java` 의 `EVAL_SERVICE_KIND_MAP`(132-140행)과 같은 값을 유지한다.
 * 차용 판정은 이 상수 하나로만 한다 — 화면마다 다시 쓰지 않는다.
 * 여기 없는 시설 유형(DEMENTIA_CARE·HOME_CARE_CENTER·NURSING_HOSPITAL·OTHER 등)은
 * 자기 급여종류가 없으므로 급여종류가 있으면 항상 차용이다.
 */
const EVAL_SERVICE_KIND_FACILITY_TYPE: ReadonlyMap<string, string> = new Map([
  ['입소시설30인이상', 'NURSING_HOME'],
  ['입소시설10이상30인미만', 'NURSING_HOME'],
  ['입소시설10인미만', 'NURSING_HOME_SMALL'],
  ['방문요양', 'HOME_VISIT_CARE'],
  ['방문목욕', 'HOME_VISIT_BATH'],
  ['방문간호', 'HOME_VISIT_NURSING'],
  ['주야간보호', 'DAY_CARE'],
  ['단기보호', 'SHORT_STAY'],
  ['복지용구', 'WELFARE_EQUIPMENT'],
])

/** 입소시설 급여종류 표시 이름 (§2.6). 저장 값·API 값은 그대로 두고 표시 단계에서만 바꾼다. */
const SERVICE_KIND_DISPLAY_NAMES: ReadonlyMap<string, string> = new Map([
  ['입소시설30인이상', '입소시설(30인 이상)'],
  ['입소시설10이상30인미만', '입소시설(10인 이상 30인 미만)'],
  ['입소시설10인미만', '입소시설(10인 미만)'],
])

/**
 * 급여종류 표시 이름. 입소시설 3종만 띄어쓰기·괄호를 넣은 이름으로 바꾸고 그 밖의 값은 그대로 둔다.
 * 입력은 정규화(공백·숫자 접두사 제거)를 거친 뒤 변환한다. 빈 값이면 `''`.
 */
export function toEvaluationServiceKindDisplayName(evaluationServiceKind: string | null | undefined): string {
  const serviceKind = normalizeServiceKind(evaluationServiceKind)
  return serviceKind === null ? '' : displayNameOf(serviceKind)
}

/** 정규화된 급여종류의 표시 이름. */
function displayNameOf(serviceKind: string): string {
  return SERVICE_KIND_DISPLAY_NAMES.get(serviceKind) ?? serviceKind
}

/**
 * 차용 평가 여부 — 급여종류가 시설 유형의 자기 급여종류가 아니면 true.
 * 급여종류가 없으면 알 수 없으므로 false(기존 문구 유지, §2.3). 시설 유형이 없으면 false(하위 호환).
 * 판정은 정규화한 **저장 값** 기준이다(표시 이름 변환 전).
 */
export function isBorrowedEvaluation(
  facilityType: string | null | undefined,
  evaluationServiceKind: string | null | undefined,
): boolean {
  const type = clean(facilityType)
  const serviceKind = normalizeServiceKind(evaluationServiceKind)
  if (type === null || serviceKind === null) return false
  return isBorrowedKind(type, serviceKind)
}

/** 정규화된 값끼리 차용 판정. 매핑에 없는 급여종류이거나 다른 유형의 급여종류면 차용. */
function isBorrowedKind(facilityType: string, serviceKind: string): boolean {
  return EVAL_SERVICE_KIND_FACILITY_TYPE.get(serviceKind) !== facilityType
}

interface ResolvedServiceKind {
  /** 화면에 쓸 급여종류 이름 */
  name: string
  /** 다른 급여종류 평가를 빌려 온 경우 true */
  borrowed: boolean
}

/**
 * 정규화(§2.4) → 차용 판정(§2.5, 저장 값 기준) → 표시 이름(§2.6) 순서로 급여종류를 해석한다.
 * `facilityType` 이 없으면 정규화만 하고 기존 표기를 그대로 쓴다(하위 호환).
 */
function resolveServiceKind(
  evaluationServiceKind: string | null | undefined,
  facilityType: string | null | undefined,
): ResolvedServiceKind | null {
  const serviceKind = normalizeServiceKind(evaluationServiceKind)
  if (serviceKind === null) return null
  const type = clean(facilityType)
  if (type === null) return { name: serviceKind, borrowed: false }
  return { name: displayNameOf(serviceKind), borrowed: isBorrowedKind(type, serviceKind) }
}

/**
 * 기관명 없이 평가 기준만 표기. 기관명이 이미 문맥에 있는 곳(비교표 행, 어드민)에 쓴다.
 *
 * - 평가구분 + 급여종류 → `2024년 정기평가 · 방문요양`
 * - 차용(facilityType 전달 시) → `2024년 정기평가 · 방문목욕 급여 기준`
 * - 급여종류 없음        → `2024년 정기평가`
 * - 평가구분 없음        → `''` (호출부가 렌더를 생략한다)
 */
export function buildEvaluationBasisLabel(
  evaluationCategory: string | null | undefined,
  evaluationServiceKind: string | null | undefined,
  facilityType?: string | null,
): string {
  const category = clean(evaluationCategory)
  if (category === null) return ''
  const serviceKind = resolveServiceKind(evaluationServiceKind, facilityType)
  if (serviceKind === null) return category
  return serviceKind.borrowed
    ? `${category} · ${serviceKind.name} 급여 기준`
    : `${category} · ${serviceKind.name}`
}

/**
 * `평가등급 출처:` 캡션용 — 기관명 + 평가 기준.
 *
 * - 평가구분 + 급여종류 → `국민건강보험공단 2024년 정기평가 · 방문요양`
 * - 차용(facilityType 전달 시) → `국민건강보험공단 2024년 정기평가 · 방문목욕 급여 기준`
 * - 급여종류 없음        → `국민건강보험공단 2024년 정기평가`
 * - 평가구분 없음        → `''` (호출부가 캡션 자체를 렌더하지 않는다)
 */
export function buildEvaluationSourceLabel(
  evaluationCategory: string | null | undefined,
  evaluationServiceKind: string | null | undefined,
  facilityType?: string | null,
): string {
  const basis = buildEvaluationBasisLabel(evaluationCategory, evaluationServiceKind, facilityType)
  return basis === '' ? '' : `${EVALUATION_SOURCE_ORG} ${basis}`
}

/**
 * 문장 안에 넣는 출처 구문 (`…에서 A등급을 받았습니다`).
 *
 * - 평가구분 + 급여종류 → `국민건강보험공단 2024년 정기평가(방문요양)`
 * - 차용(facilityType 전달 시) → `국민건강보험공단 2024년 정기평가(방문목욕 급여)`
 * - 급여종류 없음        → `국민건강보험공단 2024년 정기평가`
 * - 평가구분 없음        → `국민건강보험공단 평가`
 */
export function buildEvaluationSourcePhrase(
  evaluationCategory: string | null | undefined,
  evaluationServiceKind: string | null | undefined,
  facilityType?: string | null,
): string {
  const category = clean(evaluationCategory)
  if (category === null) return `${EVALUATION_SOURCE_ORG} 평가`
  const serviceKind = resolveServiceKind(evaluationServiceKind, facilityType)
  if (serviceKind === null) return `${EVALUATION_SOURCE_ORG} ${category}`
  return serviceKind.borrowed
    ? `${EVALUATION_SOURCE_ORG} ${category}(${serviceKind.name} 급여)`
    : `${EVALUATION_SOURCE_ORG} ${category}(${serviceKind.name})`
}
